// src/components/TutorialOverlay.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, SkipForward } from 'lucide-react';

export default function TutorialOverlay({ steps, currentStep, onNext, onPrev, onSkip, onFinish }) {
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [isAnimating, setIsAnimating] = useState(false);
  const tooltipRef = useRef(null);

  const step = steps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;
  const isCentered = step.position === 'center' || !step.target;

  // Measure the target element position (no scrolling)
  const measureTarget = useCallback(() => {
    if (!step.target) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    } else {
      setTargetRect(null);
    }
  }, [step.target]);

  // On step change: scroll target into view, then measure after scroll settles
  useEffect(() => {
    setIsAnimating(true);
    const animTimer = setTimeout(() => setIsAnimating(false), 300);

    if (!step.target) {
      setTargetRect(null);
      return () => clearTimeout(animTimer);
    }

    const el = document.querySelector(step.target);
    if (el) {
      // Scroll element into view first
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Measure immediately for a quick initial position
      measureTarget();

      // Re-measure after scroll animation completes to get final position
      const scrollTimer = setTimeout(measureTarget, 400);
      const settleTimer = setTimeout(measureTarget, 700);

      // Also listen for scroll/resize for continuous updates
      window.addEventListener('resize', measureTarget);
      window.addEventListener('scroll', measureTarget, true);

      return () => {
        clearTimeout(animTimer);
        clearTimeout(scrollTimer);
        clearTimeout(settleTimer);
        window.removeEventListener('resize', measureTarget);
        window.removeEventListener('scroll', measureTarget, true);
      };
    } else {
      setTargetRect(null);
      return () => clearTimeout(animTimer);
    }
  }, [currentStep, step.target, measureTarget]);

  // Position tooltip relative to the target
  useEffect(() => {
    if (isCentered || !targetRect) {
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      });
      return;
    }

    const padding = 16;
    const tooltipWidth = 340;
    // Use actual tooltip height if available, otherwise estimate
    const tooltipHeight = tooltipRef.current?.offsetHeight || 240;

    let top, left;

    switch (step.position) {
      case 'right':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.left + targetRect.width + padding;
        break;
      case 'left':
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.left - tooltipWidth - padding;
        break;
      case 'top':
        top = targetRect.top - tooltipHeight - padding;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        break;
      case 'bottom':
      default:
        top = targetRect.top + targetRect.height + padding;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        break;
    }

    // Clamp to viewport with generous margin
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));

    // If tooltip would overlap with the target (e.g., bottom position but not enough space),
    // try flipping to the opposite side
    if (step.position === 'bottom' && top < targetRect.top + targetRect.height) {
      // Not enough room below, try above
      const aboveTop = targetRect.top - tooltipHeight - padding;
      if (aboveTop >= padding) {
        top = aboveTop;
      }
    } else if (step.position === 'top' && top + tooltipHeight > targetRect.top) {
      // Not enough room above, try below
      const belowTop = targetRect.top + targetRect.height + padding;
      if (belowTop + tooltipHeight <= window.innerHeight - padding) {
        top = belowTop;
      }
    }

    setTooltipStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
    });
  }, [targetRect, step.position, isCentered]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onSkip();
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (isLast) onFinish();
        else onNext();
      }
      if (e.key === 'ArrowLeft' && !isFirst) onPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isFirst, isLast, onNext, onPrev, onSkip, onFinish]);

  // Build the spotlight clip-path
  const getClipPath = () => {
    if (!targetRect) return 'none';
    const p = 8; // padding around target
    const r = 12; // border radius
    const x = targetRect.left - p;
    const y = targetRect.top - p;
    const w = targetRect.width + p * 2;
    const h = targetRect.height + p * 2;

    // Outer rect covers viewport, inner rect is the cutout
    return `polygon(
      0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%,
      ${x}px ${y + r}px,
      ${x + r}px ${y}px,
      ${x + w - r}px ${y}px,
      ${x + w}px ${y + r}px,
      ${x + w}px ${y + h - r}px,
      ${x + w - r}px ${y + h}px,
      ${x + r}px ${y + h}px,
      ${x}px ${y + h - r}px,
      ${x}px ${y + r}px
    )`;
  };

  return (
    <div className="fixed inset-0 z-[9999]" style={{ pointerEvents: 'none' }}>
      {/* Backdrop with spotlight cutout */}
      <div
        className="absolute inset-0 transition-all duration-300"
        style={{
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          clipPath: targetRect ? getClipPath() : 'none',
          pointerEvents: 'auto',
        }}
        onClick={onSkip}
      />

      {/* Spotlight ring around target */}
      {targetRect && (
        <div
          className="absolute rounded-xl transition-all duration-300"
          style={{
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            boxShadow: '0 0 0 4000px rgba(15, 23, 42, 0.65)',
            border: '2px solid rgba(99, 102, 241, 0.6)',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`z-[10000] w-[340px] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden transition-all duration-300 ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
        style={{ ...tooltipStyle, pointerEvents: 'auto' }}
      >
        {/* Header gradient bar */}
        <div className="h-1.5 bg-gradient-to-r from-blue-500 via-violet-500 to-indigo-500" />

        {/* Content */}
        <div className="p-5">
          {/* Step counter & close */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-400 dark:text-slate-500">
              {currentStep + 1} of {steps.length}
            </span>
            <button
              onClick={onSkip}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              title="Skip tutorial"
            >
              <X size={16} className="text-slate-400 dark:text-slate-500" />
            </button>
          </div>

          {/* Title & description */}
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2">
            {step.title}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-5">
            {step.description}
          </p>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mb-4">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === currentStep
                    ? 'w-6 bg-blue-500'
                    : i < currentStep
                      ? 'w-1.5 bg-blue-300'
                      : 'w-1.5 bg-slate-200 dark:bg-slate-600'
                }`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <div>
              {!isFirst && (
                <button
                  onClick={onPrev}
                  className="flex items-center gap-1 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                >
                  <ChevronLeft size={16} />
                  Back
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isLast && (
                <button
                  onClick={onSkip}
                  className="flex items-center gap-1 text-xs font-medium text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400 transition-colors px-3 py-2"
                >
                  <SkipForward size={12} />
                  Skip
                </button>
              )}
              <button
                onClick={isLast ? onFinish : onNext}
                className="flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-violet-600 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all"
              >
                {isLast ? 'Get Started' : 'Next'}
                {!isLast && <ChevronRight size={16} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
